<template>
    <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-800">Program Distribution by Campus</h3>
        </div>
        <apexchart type="bar" :options="chartOptions" :series="chartSeries" height="400" />
    </div>
</template>

<script>
export default {
    name: "ProgramByCampusChart",
    props: {
        data: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    computed: {
        chartSeries() {
            // Transform data to show campuses as series and programs as categories
            const series = [];

            this.data.forEach(campus => {
                const campusData = [];
                this.programNames.forEach(programName => {
                    const program = campus.programs.find(p => p.program_name === programName);
                    campusData.push(program ? program.expected_graduate_count : 0);
                });

                series.push({
                    name: campus.campus_code,
                    data: campusData
                });
            });

            return series;
        },
        programNames() {
            // Get unique program names across all campuses
            const allPrograms = new Set();
            this.data.forEach(campus => {
                campus.programs.forEach(program => {
                    allPrograms.add(program.program_name);
                });
            });
            return Array.from(allPrograms);
        },
        chartData() {
            // Transform data to show programs on x-axis and campuses as series
            const programMap = {};

            // Initialize all programs with 0 for all campuses
            this.programNames.forEach(programName => {
                programMap[programName] = {};
                this.data.forEach(campus => {
                    programMap[programName][campus.campus_code] = 0;
                });
            });

            // Fill in actual values
            this.data.forEach(campus => {
                campus.programs.forEach(program => {
                    programMap[program.program_name][campus.campus_code] = program.expected_graduate_count;
                });
            });

            return programMap;
        },
        chartOptions() {
            return {
                chart: {
                    type: 'bar',
                    stacked: true,
                    toolbar: {
                        show: false
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800,
                        animateGradually: {
                            enabled: true,
                            delay: 150
                        },
                        dynamicAnimation: {
                            enabled: true,
                            speed: 350
                        }
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '70%',
                        dataLabels: {
                            position: 'center',
                            total: {
                                enabled: true,
                                offsetX: 0,
                                style: {
                                    fontSize: '13px',
                                    fontWeight: 900
                                }
                            }
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        // Only show labels for non-zero values
                        if (val > 0) {
                            return val.toLocaleString();
                        }
                        return '';
                    },
                    style: {
                        fontSize: '11px',
                        fontWeight: 'bold',
                        colors: ['#ffffff']
                    }
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                xaxis: {
                    categories: this.programNames,
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '600',
                            colors: '#374151'
                        }
                    },
                    title: {
                        text: 'Number of Students',
                        style: {
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151'
                        }
                    }
                },
                yaxis: {
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '600',
                            colors: '#374151'
                        }
                    }
                },
                colors: ['#0f766e', '#f59e0b', '#92400e', '#dc2626', '#7c3aed', '#059669'], // Dark teal, Gold, Brown, Red, Purple, Green
                fill: {
                    type: 'solid',
                    opacity: 1
                },
                grid: {
                    borderColor: '#e5e7eb',
                    strokeDashArray: 4,
                    xaxis: {
                        lines: {
                            show: true
                        }
                    },
                    yaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val.toLocaleString() + ' students';
                        }
                    },
                    theme: 'light',
                    style: {
                        fontSize: '12px'
                    }
                },
                legend: {
                    position: 'top',
                    horizontalAlign: 'right',
                    labels: {
                        colors: '#374151',
                        useSeriesColors: false
                    },
                    markers: {
                        width: 12,
                        height: 12,
                        radius: 6
                    }
                },
                responsive: [
                    {
                        breakpoint: 768,
                        options: {
                            plotOptions: {
                                bar: {
                                    barHeight: '60%'
                                }
                            },
                            dataLabels: {
                                fontSize: '10px'
                            },
                            xaxis: {
                                labels: {
                                    fontSize: '11px'
                                }
                            },
                            legend: {
                                position: 'top',
                                horizontalAlign: 'center'
                            }
                        }
                    }
                ]
            };
        }
    }
};
</script>

<style scoped>
/* Component-specific styles can be added here */
</style>